// SetValueCell — one enabled set's value for one form row.
//
// A native `Select`, deliberately, and the first value-picking control in a
// cell in this repo. Every menu in this table otherwise needs `portal` because
// the table scrolls horizontally and an in-flow panel is clipped by that
// container — a native <select> has no such problem, since the browser draws
// its option list outside the document. It is also keyboard- and
// screen-reader-operable without any of the focus work a hand-rolled Dropdown
// would need here.
//
// `fitContainer` is not cosmetic: a native select's min-content is its LONGEST
// option, and .data-table__cell carries no min-width: 0, so without it one long
// option would widen the whole table. It pairs with the fixed track width the
// column declares, and Select resolves a `title` from the selected label so the
// truncated text stays readable.
//
// Purely presentational: the caller resolves the value — a local draft if the
// user has touched this cell, otherwise the row's own saved pick — and every
// change is reported straight back up (the DbNameCell arrangement).
//
// The option LABEL comes from the catalogue, never from the row. A value with no
// catalogue entry therefore renders as the placeholder rather than as itself.
//
// Deliberately generic over its row and its column: the Form meta & SQL tab
// feeds it a form + an option set, the Form structure tab a FIELD + either an
// option set or the topic catalogue. It needs none of those types — only a row
// to report against, two labels, and the options — so it takes exactly that.
// The set id is absent from `onChange` because each column's closure already
// knows its own.

import { useTranslation } from 'react-i18next';
import { Select } from '@/components/common';

interface SetValueCellProps {
  /** Reported back on change; whatever the caller keys its drafts by. */
  rowId: string;
  /** The row's own name — ten rows would otherwise announce ten "Select"s. */
  rowLabel: string;
  /** The column's name: a set title, or Topics. */
  groupLabel: string;
  options: { id: string; value: string }[];
  /** The drafted option id, or '' for nothing picked yet. */
  value: string;
  onChange: (rowId: string, optionId: string) => void;
}

const SetValueCell = ({
  rowId,
  rowLabel,
  groupLabel,
  options,
  value,
  onChange,
}: SetValueCellProps) => {
  const { t } = useTranslation();

  return (
    <Select
      size='sm'
      fitContainer
      value={value}
      aria-label={t('data_setup_sets_cell_label', { set: groupLabel, form: rowLabel })}
      onChange={(e) => onChange(rowId, e.target.value)}
      options={[
        // `placeholder` is not a real <select> attribute — an empty-value
        // leading option is the house stand-in.
        { value: '', label: t('data_setup_sets_select_ph') },
        ...options.map((o) => ({ value: o.id, label: o.value })),
      ]}
    />
  );
};

export default SetValueCell;
